import cn from "classnames";
import type { FormikProps } from "formik";
import { Box, InputAdornment, TextField, FormControlLabel, Switch, Typography } from "@mui/material";
import Link from "next/link";
import Image from "next/image";

import { DatePicker } from "@/src/component/base/DatePicker";
import { Dropdown } from "@/src/component/base/Dropdown";
import { ProviderHooks } from "@/src/hook";
import { StrapiCollectionType } from "@/src/type";
import { FormItemType } from "@/src/type/strapi/collection/FormItem";
import { FormHelper, LoadFromCDN } from "@/src/util";

import { FormStyles } from "@/styles/util";

import { PhoneNumber } from "../PhoneNumber";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./FormItem.module.scss";

interface FormItemProps {
  item: StrapiCollectionType.FormItem;
  isDark?: boolean;
  disableAnimatedLabel?: boolean;
  formik: FormikProps<any>;
}

function FormItem({ item, isDark, disableAnimatedLabel, formik }: FormItemProps) {
  const cms = ProviderHooks.useCMS();
  const propertyName = item.definedProperty ?? item.jsonPropertyName;
  if (!propertyName) {
    return <></>;
  }

  function getHref(href: string, isExternal: boolean): string {
    return isExternal ? href : cms.constructLink(href);
  }

  switch (item.type ?? "") {
    case FormItemType.Text:
    case FormItemType.Number:
      return (
        <TextField
          fullWidth
          id={`${item.id}-${item.name}`}
          name={propertyName}
          label={item.label}
          aria-label={item.label}
          placeholder={item.placeholder}
          value={formik.values[propertyName] ?? ""}
          onChange={formik.handleChange}
          InputProps={{
            startAdornment: item.startIcon?.data?.attributes?.url ? (
              <InputAdornment position="start">
                <Image
                  src={item.startIcon?.data?.attributes?.url}
                  loader={LoadFromCDN.loaderForNextJS}
                  width={24}
                  height={24}
                  quality={100}
                  alt={`${item.label} Icon`}
                />
              </InputAdornment>
            ) : undefined,
          }}
          helperText={FormHelper.formikErrorMessage<any>(formik, propertyName)}
          error={FormHelper.formikCheckError<any>(formik, propertyName)}
          inputProps={{
            maxLength: item.validation?.max,
            minLength: item.validation?.min,
            inputMode: item.type === FormItemType.Number ? "tel" : undefined,
          }}
          sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values[propertyName], disableAnimatedLabel) }}
        />
      );
    case FormItemType.TextArea:
      return (
        <TextField
          multiline={true}
          rows={item.textAreaLines ?? 4}
          fullWidth
          id={`${item.id}-${item.name}`}
          name={propertyName}
          label={item.label}
          aria-label={item.label}
          placeholder={item.placeholder}
          value={formik.values[propertyName] ?? ""}
          onChange={formik.handleChange}
          helperText={FormHelper.formikErrorMessage<any>(formik, propertyName)}
          error={FormHelper.formikCheckError<any>(formik, propertyName)}
          inputProps={{
            maxLength: item.validation?.max,
            minLength: item.validation?.min,
            name: propertyName,
          }}
          sx={{
            ...FormStyles.themeStyle(isDark),
            ...(disableAnimatedLabel ? FormStyles.handleLabel(formik.values[propertyName], disableAnimatedLabel) : undefined),
          }}
        />
      );
    case FormItemType.Dropdown:
      return (
        <Dropdown
          id={`${item.id}-${item.name}`}
          name={propertyName}
          label={item.label}
          aria-label={item.label}
          placeholder={item.placeholder}
          value={formik.values[propertyName] ?? ""}
          options={(item.dropdownOptions ?? []).map((opt) => ({ value: opt.value ?? opt.title, label: opt.title }))}
          onChange={(e) => {
            formik.setFieldValue(propertyName, e.target.value, true);
          }}
          startAdornment={
            item.startIcon?.data?.attributes?.url ? (
              <InputAdornment position="start">
                <Image
                  src={item.startIcon?.data?.attributes?.url}
                  loader={LoadFromCDN.loaderForNextJS}
                  width={24}
                  height={24}
                  quality={100}
                  alt={`${item.label} Icon`}
                />
              </InputAdornment>
            ) : undefined
          }
          helperText={FormHelper.formikErrorMessage<any>(formik, propertyName)}
          error={FormHelper.formikCheckError<any>(formik, propertyName)}
          sx={{
            formControl: FormStyles.handleLabel(formik.values[propertyName], disableAnimatedLabel),
          }}
        />
      );
    case FormItemType.PhoneNumber:
      return (
        <PhoneNumber
          item={item}
          isDark={isDark}
          disableAnimatedLabel={disableAnimatedLabel}
          formik={formik}
        />
      );
    case FormItemType.DatePicker:
      return (
        <Box sx={FormStyles.handleLabel(formik.values[propertyName], disableAnimatedLabel)}>
          <DatePicker
            name={propertyName}
            views={["year", "month", "day"]}
            yearsPerRow={3}
            aria-label={item.label}
            label={item.label}
            value={formik.values[propertyName]}
            onChange={(val) => {
              formik.setFieldValue(propertyName, val);
            }}
            helperText={FormHelper.formikErrorMessage<any>(formik, propertyName)}
            error={FormHelper.formikCheckError<any>(formik, propertyName)}
          />
        </Box>
      );
    case FormItemType.Checkbox:
      return (
        <div className={styles.checkboxContainer}>
          <FormControlLabel
            control={
              <Switch
                name={propertyName}
                checked={formik.values[propertyName]}
                onChange={() => {
                  formik.setFieldValue(propertyName, !formik.values[propertyName]);
                }}
                sx={FormStyles.checkboxStyle(isDark)}
              />
            }
            label={
              <div className={styles.disclaimerCheckboxLabel}>
                <Typography
                  className={cn(styles.checkboxLabel)}
                  color={isDark ? theme.primaryMain : theme.secondaryMain}
                >
                  {item.extendedLabel ?? item.label}
                  {(item?.extendedLabelLinks ?? []).map((link: any, i: any) => (
                    <span key={i}>
                      {i > 0 ? " & " : " "}
                      <Link
                        key={i}
                        href={getHref(link.href, link.isExternal)}
                        target={link.target ?? "_blank"}
                      >
                        {link.label}
                      </Link>
                    </span>
                  ))}
                </Typography>
              </div>
            }
          />
        </div>
      );
    default:
      return <></>;
  }
}

export default FormItem;
